/* ==========================================================================
   Sproutling cart drawer behaviour
   --------------------------------------------------------------------------
   Hooks into the events Pipeline already fires (theme:cart:change and
   theme:cart:reload) rather than patching theme.js, so a theme update will
   not clobber any of this.

   Responsibilities:
     - keep the free shipping bar in sync and fire confetti on the crossing
     - re-render the summary server side, since /cart.js has no compare_at
     - drive the shipping protection toggle
     - keep the instalment amount current
   ========================================================================== */

(function () {
  'use strict';

  var SELECTORS = {
    drawer: '[data-drawer="drawer-cart"]',
    shipBar: '[data-free-shipping-bar]',
    shipMarker: '[data-ship-marker]',
    shipTrack: '[data-ship-track]',
    shipRemaining: '[data-ship-remaining]',
    summaryWrapper: '[data-cart-summary-wrapper]',
    summary: '[data-cart-summary]',
    summaryTotal: '[data-summary-total]',
    protection: '[data-shipping-protection]',
    protectionToggle: '[data-protection-toggle]',
    bnplWrapper: '[data-cart-bnpl-wrapper]',
    bnpl: '[data-cart-bnpl]',
    bnplAmount: '[data-bnpl-amount]',
    count: '[data-cart-count]',
    apiContent: '[data-api-content]',
    ajaxFlag: '[data-ajax-disable]'
  };

  // Third-party widgets that float above the drawer and cover the checkout
  // button. Add further selectors here to hide them alongside the chat.
  var DRAWER_OPEN_CLASS = 'cart-drawer-open';
  var VISIBLE_CLASS = 'drawer--visible';

  var STORAGE_KEY = 'sproutling:shipping-protection';

  var reducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- utils */

  function money(cents) {
    var vendor = window.themeVendor;
    var format = window.theme && window.theme.moneyFormat;
    if (vendor && vendor.themeCurrency && format) {
      return vendor.themeCurrency.formatMoney(Math.round(cents), format);
    }
    return '$' + (Math.round(cents) / 100).toFixed(2);
  }

  function readBool(value) {
    return String(value) === 'true';
  }

  // Publishes a fresh cart to the theme, then rebuilds the drawer markup.
  //
  // theme:cart:reload on its own reuses the theme's cached cart object. When
  // that cache is behind - right after an add, or after this file changed the
  // cart itself - reloading first renders the previous cart, which shows the
  // empty state on the very first add. Fetching and firing theme:cart:change
  // updates the cache (and this drawer) before the rebuild.
  function refreshCart(done) {
    return window
      .fetch(window.theme.routes.cart + '.js')
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        document.dispatchEvent(
          new CustomEvent('theme:cart:change', { detail: { cart: cart }, bubbles: true })
        );
      })
      .catch(function () {
        /* the reload below still re-fetches the line items from Liquid */
      })
      .then(function () {
        if (typeof done === 'function') done();
        document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
      });
  }

  /* ------------------------------------------------------------ confetti */

  // Small self-contained burst. No library, no persistent canvas: the canvas
  // is created for the run and removed when the last piece falls out of view.
  var DEFAULT_CONFETTI = ['#e1b633', '#f4a482', '#2f8f5b', '#ffffff', '#bc6c28', '#e8c86a'];

  function confetti(host, source, colors) {
    if (reducedMotion || !host) return;

    var canvas = document.createElement('canvas');
    canvas.className = 'cart-confetti';
    canvas.setAttribute('aria-hidden', 'true');
    host.appendChild(canvas);

    var ctx = canvas.getContext('2d');
    var dpr = window.devicePixelRatio || 1;
    var width = host.offsetWidth;
    var height = host.offsetHeight;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';
    ctx.scale(dpr, dpr);

    colors = colors && colors.length ? colors : DEFAULT_CONFETTI;
    var pieces = [];
    var count = 110;

    // Two jets angled up and inward, fired from the lower corners of whatever
    // element triggered the burst - the progress bar - so the confetti reads
    // as coming out of it rather than from nowhere.
    var hostRect = host.getBoundingClientRect();
    var rect = source ? source.getBoundingClientRect() : hostRect;
    var originY = rect.bottom - hostRect.top;
    var leftX = rect.left - hostRect.left + rect.width * 0.12;
    var rightX = rect.left - hostRect.left + rect.width * 0.88;

    var origins = [
      { x: leftX, y: originY, spread: -0.35 },
      { x: rightX, y: originY, spread: 0.35 }
    ];

    for (var i = 0; i < count; i++) {
      var origin = origins[i % origins.length];
      var angle = -Math.PI / 2 + origin.spread + (Math.random() - 0.5) * 0.9;
      var velocity = 7 + Math.random() * 7;

      pieces.push({
        x: origin.x,
        y: origin.y,
        vx: Math.cos(angle) * velocity,
        vy: Math.sin(angle) * velocity,
        size: 5 + Math.random() * 5,
        color: colors[(Math.random() * colors.length) | 0],
        rotation: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * 0.28,
        wobble: Math.random() * Math.PI * 2,
        life: 0
      });
    }

    var gravity = 0.24;
    var drag = 0.987;
    var maxLife = 190;
    var frame;

    function tick() {
      ctx.clearRect(0, 0, width, height);
      var alive = 0;

      for (var i = 0; i < pieces.length; i++) {
        var p = pieces[i];
        if (p.life > maxLife) continue;

        p.life++;
        p.vx *= drag;
        p.vy = p.vy * drag + gravity;
        p.wobble += 0.1;
        p.x += p.vx + Math.sin(p.wobble) * 0.6;
        p.y += p.vy;
        p.rotation += p.spin;

        if (p.y > height + 40) continue;
        alive++;

        var fade = 1 - Math.max(0, (p.life - maxLife * 0.62) / (maxLife * 0.38));

        ctx.save();
        ctx.globalAlpha = Math.max(0, fade);
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.fillStyle = p.color;
        // Squashing the height as it spins reads as a tumbling paper flake.
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * Math.abs(Math.cos(p.wobble)));
        ctx.restore();
      }

      if (alive > 0) {
        frame = window.requestAnimationFrame(tick);
      } else {
        window.cancelAnimationFrame(frame);
        if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
      }
    }

    frame = window.requestAnimationFrame(tick);
  }

  /* --------------------------------------------------- free shipping bar */

  function FreeShippingBar(drawer) {
    this.drawer = drawer;
    this.el = drawer.querySelector(SELECTORS.shipBar);
    if (!this.el) return;

    this.threshold = parseInt(this.el.getAttribute('data-threshold'), 10) || 0;
    this.marker = this.el.querySelector(SELECTORS.shipMarker);
    this.track = this.el.querySelector(SELECTORS.shipTrack);
    this.remaining = this.el.querySelector(SELECTORS.shipRemaining);
    this.confettiEnabled = readBool(this.el.getAttribute('data-confetti'));
    this.confettiColors = (this.el.getAttribute('data-confetti-colors') || '')
      .split(',')
      .map(function (value) {
        return value.trim();
      })
      .filter(Boolean);

    // Seeded from the server-rendered state so an already-qualifying cart
    // does not fire confetti the moment the drawer opens.
    this.unlocked = readBool(this.el.getAttribute('data-unlocked'));
  }

  FreeShippingBar.prototype.update = function (totalPrice) {
    if (!this.el || !this.threshold) return;

    var percent = Math.max(0, Math.min(100, (totalPrice / this.threshold) * 100));
    var remainingCents = Math.max(0, this.threshold - totalPrice);
    var nowUnlocked = totalPrice >= this.threshold;

    if (this.track) {
      this.track.style.setProperty('--progress', percent);
      this.track.setAttribute('aria-valuenow', Math.round(percent));
    }
    if (this.remaining) this.remaining.innerHTML = money(remainingCents);

    this.el.classList.toggle('is-unlocked', nowUnlocked);

    // Only celebrate the transition, and re-arm if the cart drops back below.
    if (nowUnlocked && !this.unlocked) {
      this.celebrate();
    }

    this.unlocked = nowUnlocked;
    this.el.setAttribute('data-unlocked', String(nowUnlocked));
  };

  FreeShippingBar.prototype.celebrate = function () {
    var el = this.el;

    el.classList.add('is-celebrating');
    window.setTimeout(function () {
      el.classList.remove('is-celebrating');
    }, 700);

    if (!this.confettiEnabled) return;

    // Let the bar finish travelling before the burst goes off.
    var host = this.drawer.querySelector('.drawer__content') || this.drawer;
    var colors = this.confettiColors;
    window.setTimeout(function () {
      confetti(host, el, colors);
    }, reducedMotion ? 0 : 320);
  };

  /* ------------------------------------------------- shipping protection */

  function ShippingProtection(drawer) {
    this.drawer = drawer;
    this.el = drawer.querySelector(SELECTORS.protection);
    if (!this.el) return;

    this.toggle = this.el.querySelector(SELECTORS.protectionToggle);
    this.isPreview = readBool(this.el.getAttribute('data-preview'));
    this.variantId = this.el.getAttribute('data-variant-id');
    this.priceCents = parseInt(this.el.getAttribute('data-price'), 10) || 0;

    if (this.isPreview) this.restore();
    this.bind();
  }

  ShippingProtection.prototype.isOn = function () {
    return this.toggle && this.toggle.getAttribute('aria-checked') === 'true';
  };

  ShippingProtection.prototype.setState = function (on) {
    if (!this.toggle) return;
    this.toggle.setAttribute('aria-checked', String(on));
    this.el.setAttribute('data-checked', String(on));
  };

  // Preview mode has nothing in the cart to read back, so the choice is
  // remembered locally to survive a reload.
  ShippingProtection.prototype.restore = function () {
    try {
      var stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored !== null) this.setState(stored === 'true');
    } catch (e) {
      /* private browsing, storage blocked - keep the rendered default */
    }
  };

  ShippingProtection.prototype.remember = function (on) {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(on));
    } catch (e) {
      /* nothing to do, the toggle still works for this page view */
    }
  };

  ShippingProtection.prototype.bind = function () {
    if (!this.toggle) return;
    var self = this;

    this.toggle.addEventListener('click', function () {
      var next = !self.isOn();

      if (self.isPreview) {
        // No product wired up yet: reflect the choice, adjust the displayed
        // total, and leave the real cart alone.
        self.setState(next);
        self.remember(next);
        document.dispatchEvent(new CustomEvent('sproutling:protection:change'));
        return;
      }

      self.setState(next);
      self.el.classList.add('is-busy');
      next ? self.addToCart() : self.removeFromCart();
    });
  };

  ShippingProtection.prototype.finish = function () {
    var self = this;
    refreshCart(function () {
      self.el.classList.remove('is-busy');
    });
  };

  ShippingProtection.prototype.addToCart = function () {
    var self = this;
    window
      .fetch(window.theme.routes.cart + '/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(this.variantId), quantity: 1 }] })
      })
      .then(function () {
        self.finish();
      })
      .catch(function () {
        self.setState(false);
        self.finish();
      });
  };

  ShippingProtection.prototype.removeFromCart = function () {
    var self = this;
    window
      .fetch(window.theme.routes.cart + '/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: String(this.variantId), quantity: 0 })
      })
      .then(function () {
        self.finish();
      })
      .catch(function () {
        self.finish();
      });
  };

  ShippingProtection.prototype.extraCents = function () {
    // Only preview mode adds to the total here; a real product is already a
    // line item and is counted in cart.total_price.
    return this.isPreview && this.isOn() ? this.priceCents : 0;
  };

  /* -------------------------------------------------- overlay suppression */

  // Marks the document while the drawer is open so CSS can hide overlays
  // such as the Shopify Inbox chat bubble.
  //
  // This watches the class the theme toggles rather than its
  // theme:drawer:open / theme:drawer:close events: closing with Escape or by
  // clicking the underlay calls hideDrawer() directly and never dispatches
  // the close event, which would leave the chat hidden for good.
  function OverlaySuppressor(drawer) {
    if (!readBool(drawer.getAttribute('data-hide-chat'))) return;

    var root = document.documentElement;

    function sync() {
      root.classList.toggle(DRAWER_OPEN_CLASS, drawer.classList.contains(VISIBLE_CLASS));
    }

    new MutationObserver(sync).observe(drawer, {
      attributes: true,
      attributeFilter: ['class']
    });

    sync();
  }

  /* ------------------------------------------------- open on add to cart */

  // Replaces the "View Cart" banner with the drawer itself, so adding to
  // cart takes one click instead of two.
  //
  // After an add, the theme dispatches theme:cart:popdown and a listener on
  // document renders the banner. Listening on window in the capture phase
  // puts this ahead of that listener, so stopping the event there prevents
  // the banner from ever being built.
  function AutoOpen(drawer) {
    if (!readBool(drawer.getAttribute('data-auto-open'))) return;

    window.addEventListener(
      'theme:cart:popdown',
      function (event) {
        // On the cart page the same event means "refresh", not "show a
        // banner". Leave the theme to handle it.
        if (document.querySelector(SELECTORS.ajaxFlag)) return;

        event.stopPropagation();

        // Open straight away so the drawer animates in while the cart is
        // still being fetched, rather than after a visible pause.
        drawer.dispatchEvent(new CustomEvent('theme:drawer:open', { bubbles: false }));
        refreshCart();
      },
      true
    );
  }

  /* ---------------------------------------------------------- the drawer */

  function CartDrawer(drawer) {
    this.drawer = drawer;
    this.shipBar = new FreeShippingBar(drawer);
    this.protection = new ShippingProtection(drawer);
    OverlaySuppressor(drawer);
    AutoOpen(drawer);
    this.summaryWrapper = drawer.querySelector(SELECTORS.summaryWrapper);
    this.bnplWrapper = drawer.querySelector(SELECTORS.bnplWrapper);
    this.counts = document.querySelectorAll(SELECTORS.count);
    this.cart = null;
    this.listen();

    // The server renders the summary with the protection default folded in.
    // If this shopper previously turned it off, correct the total straight
    // away rather than waiting for the first cart event.
    this.applyTotals();
  }

  CartDrawer.prototype.listen = function () {
    var self = this;

    document.addEventListener('theme:cart:change', function (event) {
      if (!event.detail || !event.detail.cart) return;
      self.cart = event.detail.cart;
      self.render();
    });

    document.addEventListener('sproutling:protection:change', function () {
      self.applyTotals();
    });
  };

  CartDrawer.prototype.render = function () {
    this.updateCount();
    if (this.shipBar) this.shipBar.update(this.cart.total_price);
    this.refreshSummary();
  };

  CartDrawer.prototype.updateCount = function () {
    var count = this.cart.item_count;
    Array.prototype.forEach.call(this.counts, function (el) {
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
    });
  };

  // Compare-at savings are not in /cart.js, so the summary comes back from
  // Liquid. The instalment amount is derived from the refreshed total.
  CartDrawer.prototype.refreshSummary = function () {
    if (!this.summaryWrapper) {
      this.applyTotals();
      return;
    }

    var self = this;
    var root = (window.theme && window.theme.routes && window.theme.routes.root_url) || '/';

    window
      .fetch(root + '?section_id=api-cart-summary')
      .then(function (response) {
        if (!response.ok) throw new Error('summary fetch failed');
        return response.text();
      })
      .then(function (html) {
        var fresh = document.createElement('div');
        fresh.innerHTML = html;
        var content = fresh.querySelector(SELECTORS.apiContent);
        if (content) self.summaryWrapper.innerHTML = content.innerHTML;
        self.applyTotals();
      })
      .catch(function () {
        // Leave the last good summary in place and still correct the total.
        self.applyTotals();
      });
  };

  // Folds preview-mode protection into the displayed total and keeps the
  // instalment line in step with whatever the total ended up being.
  CartDrawer.prototype.applyTotals = function () {
    var summary = this.drawer.querySelector(SELECTORS.summary);
    var extra = this.protection ? this.protection.extraCents() : 0;
    var total = null;

    if (summary) {
      var base = parseInt(summary.getAttribute('data-cart-total'), 10) || 0;
      var shipping = parseInt(summary.getAttribute('data-shipping'), 10) || 0;
      var showShipping = readBool(summary.getAttribute('data-show-shipping'));

      total = base + extra + (showShipping ? shipping : 0);

      var totalEl = summary.querySelector(SELECTORS.summaryTotal);
      if (totalEl) totalEl.textContent = money(total);
    } else if (this.cart) {
      total = this.cart.total_price + extra;
    }

    this.updateBnpl(total);
  };

  CartDrawer.prototype.updateBnpl = function (total) {
    if (!this.bnplWrapper || total === null) return;

    var bnpl = this.bnplWrapper.querySelector(SELECTORS.bnpl);
    if (!bnpl) return;

    var instalments = parseInt(bnpl.getAttribute('data-instalments'), 10) || 4;
    var min = parseInt(bnpl.getAttribute('data-min'), 10) || 0;
    var max = parseInt(bnpl.getAttribute('data-max'), 10) || 0;

    var eligible = total >= min && (max === 0 || total <= max);
    bnpl.hidden = !eligible;

    var amount = bnpl.querySelector(SELECTORS.bnplAmount);
    if (amount) amount.textContent = money(total / instalments);
  };

  /* ------------------------------------------------------------- startup */

  function init() {
    var drawer = document.querySelector(SELECTORS.drawer);
    if (!drawer || drawer.cartDrawerCustom) return;
    drawer.cartDrawerCustom = new CartDrawer(drawer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
